import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Badge,
  Input,
  Text,
  Select,
  useDisclosure
} from '@chakra-ui/react';
import { api } from '../../utils/api';
import { FormControl, FormLabel } from '@chakra-ui/form-control';
import { VStack } from '@chakra-ui/layout';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton
} from '@chakra-ui/modal';
import { useToast } from '@chakra-ui/toast';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/table';
import { format } from 'date-fns';
import styled from 'styled-components';

interface Subscription {
  _id: string;
  schoolId: string;
  schoolName: string;
  mpesaNumber: string;
  amount: number;
  transactionId: string;
  status: 'pending' | 'active' | 'failed' | 'expired';
  paymentDate: string;
  receiptSent: boolean;
  plan: 'basic' | 'premium';
  teachers: Array<{
    teacherId: string;
    name: string;
    email: string;
    isActive: boolean;
  }>;
}

const StyledInput = styled.input`
`;

const PaymentManagement: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Form state for manual payment
  const [manualPayment, setManualPayment] = useState({
    amount: '',
    mpesaNumber: '',
    transactionId: ''
  });

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const response = await fetch('/api/subscriptions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      // Ensure data is an array before setting it
      if (!Array.isArray(data)) {
        console.error('Expected array of subscriptions but got:', data);
        setSubscriptions([]);
        toast({
          title: 'Error',
          description: 'Invalid response format from server',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      setSubscriptions(data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setSubscriptions([]);
      toast({
        title: 'Error',
        description: 'Failed to fetch subscriptions',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (subscription: Subscription) => {
    try {
      const response = await fetch(`/api/subscriptions/${subscription._id}/mark-paid`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transactionId: subscription.transactionId,
          amount: subscription.amount,
          mpesaNumber: subscription.mpesaNumber
        }),
      });

      if (!response.ok) throw new Error('Failed to mark as paid');

      toast({
        title: 'Success',
        description: 'Payment marked as paid successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      fetchSubscriptions();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to mark payment as paid',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleManualPayment = async () => {
    if (!selectedSubscription) return;

    try {
      const response = await fetch(`/api/subscriptions/${selectedSubscription._id}/manual-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...manualPayment,
          amount: Number(manualPayment.amount)
        }),
      });

      if (!response.ok) throw new Error('Failed to process manual payment');

      toast({
        title: 'Success',
        description: 'Manual payment processed successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
      fetchSubscriptions();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to process manual payment',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'failed':
        return 'red';
      case 'expired':
        return 'gray';
      default:
        return 'gray';
    }
  };

  return (
    <Box p={5}>
      <Text fontSize="2xl" mb={5}>Payment Management</Text>
      
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>School</Th>
            <Th>Amount</Th>
            <Th>MPESA Number</Th>
            <Th>Status</Th>
            <Th>Payment Date</Th>
            <Th>Teachers</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {subscriptions.map((subscription) => (
            <Tr key={subscription._id}>
              <Td>{subscription.schoolName}</Td>
              <Td>KES {subscription.amount}</Td>
              <Td>{subscription.mpesaNumber}</Td>
              <Td>
                <Badge colorScheme={getStatusColor(subscription.status)}>
                  {subscription.status}
                </Badge>
              </Td>
              <Td>{format(new Date(subscription.paymentDate), 'PPp')}</Td>
              <Td>{subscription.teachers.length}</Td>
              <Td>
                {subscription.status === 'pending' && (
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={() => handleMarkAsPaid(subscription)}
                    mr={2}
                  >
                    Mark as Paid
                  </Button>
                )}
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => {
                    setSelectedSubscription(subscription);
                    setManualPayment({
                      amount: subscription.amount.toString(),
                      mpesaNumber: subscription.mpesaNumber,
                      transactionId: ''
                    });
                    onOpen();
                  }}
                >
                  Manual Payment
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Manual Payment Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Process Manual Payment</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Amount (KES)</FormLabel>
                <Input
                  value={manualPayment.amount}
                  onChange={(e) => setManualPayment({
                    ...manualPayment,
                    amount: e.target.value
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>MPESA Number</FormLabel>
                <Input
                  value={manualPayment.mpesaNumber}
                  onChange={(e) => setManualPayment({
                    ...manualPayment,
                    mpesaNumber: e.target.value
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Transaction ID</FormLabel>
                <Input
                  value={manualPayment.transactionId}
                  onChange={(e) => setManualPayment({
                    ...manualPayment,
                    transactionId: e.target.value
                  })}
                />
              </FormControl>
              <Button colorScheme="blue" onClick={handleManualPayment} width="full">
                Process Payment
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PaymentManagement; 